import { apiClient } from './api-client'
import { AdmissionSaveError } from './admission-save-error'
import { bookingWorkerFlow } from '@/src/config/flows/booking-worker.flow'
import { transformFormDataToPayload } from '@/src/lib/flow-engine/payloadTransform'
import type {
  AdmissionLoadResponse,
  AdmissionRecoverResponse,
  AdmissionSavePayload,
  AdmissionSaveResponse,
  CodeOption,
  StructureOption
} from '@/src/types/api.types'

// ─── Admissions (POST) ────────────────────────────────────────────────

export function saveWorkerStep(payload: AdmissionSavePayload) {
  return apiClient.post<AdmissionSaveResponse>('/admissions/save', payload)
}

/**
 * Convenience wrapper used by the wizard final step.
 * Transforms the legacy shape { journey, formData, submittedAt } to API format,
 * then forwards to /admissions/save with finalStep: true.
 */
export async function submitWorkerBooking(payload: {
  journey: string
  formData: Record<string, unknown>
  submittedAt: string
  currentStep?: number
  finalStep?: boolean
  draftId?: string | null
  lang?: string
}) {
  const currentStep = payload.currentStep ?? bookingWorkerFlow.steps.length
  const isFinalStep = payload.finalStep ?? true
  const transformedPayload = transformFormDataToPayload(
    payload.formData,
    bookingWorkerFlow,
    currentStep,
    isFinalStep,
    payload.draftId ?? null,
    payload.lang
  )

  const res = await apiClient.post<AdmissionSaveResponse>('/admissions/save', transformedPayload)

  if (res.error) {
    throw new AdmissionSaveError({
      returnCode: res.returnCode ?? 'UNKNOWN_ERROR',
      message: res.message ?? 'Errore nel salvataggio della domanda.',
      error: true,
      errorMessages: res.errorMessages ?? [],
      body: null
    })
  }

  return {
    success: res.returnCode === 'OK',
    message: res.returnMessage ?? '',
    data: res
  }
}

export function loadWorkerDraft(email: string, password: string) {
  return apiClient.post<AdmissionLoadResponse>('/admissions/load', {
    username: email,
    password
  })
}

export function recoverWorkerDraft(email: string, lang: string) {
  return apiClient.post<AdmissionRecoverResponse>(
    '/admissions/recover',
    { username: email, lingua: lang },
    { language: lang }
  )
}

// ─── Codes / Lookups (GET) ─────────────────────────────────────────────

export function getWorkerCities(lang: string) {
  return apiClient.get<CodeOption[]>('/codes/residenzecamplus/pnrrcitiesby/lavoratori/all', {
    lang
  })
}

export function getWorkerStructures(cityId: string, lang: string) {
  return apiClient.get<StructureOption[]>(
    `/codes/residenzecamplus/structuresby/${cityId}/lavoratori`,
    { lang }
  )
}

export function getWorkerRooms(sedeId: string, lang: string) {
  return apiClient.get<CodeOption[]>(`/codes/residenzecamplus/rooms/${sedeId}`, { lang })
}

export function getWorkerDurations(lang: string) {
  return apiClient.get<CodeOption[]>('/codes/duratalavoratori', { lang })
}

export function getWorkerMotivations(lang: string) {
  return apiClient.get<CodeOption[]>('/codes/motivosceltalavoratori', { lang })
}

export function getWorkerContracts(lang: string) {
  return apiClient.get<CodeOption[]>('/codes/tipocontrattosito', { lang })
}
